import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@tellescope/ui";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Check, ChevronDown, GripVertical, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { VitalProgram } from "../useVitalsSettings";

export interface SortableProgramRowProps {
  programId: string;
  program: VitalProgram;
  configOptions: { id: string; title: string }[];
  onTitleChange: (id: string, title: string) => void;
  onConfigIdsChange: (id: string, ids: string[]) => void;
  onRemove: (id: string) => void;
}

export const SortableProgramRow = ({
  programId,
  program,
  configOptions,
  onTitleChange,
  onConfigIdsChange,
  onRemove,
}: SortableProgramRowProps) => {
  const { t } = useTranslation("vitals");
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: programId });
  const [open, setOpen] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const toggleConfig = (configId: string) => {
    const current = program.configurationIds;
    if (current.includes(configId)) {
      onConfigIdsChange(
        programId,
        current.filter((id) => id !== configId)
      );
    } else {
      onConfigIdsChange(programId, [...current, configId]);
    }
  };

  const selectedLabels = program.configurationIds
    .map((id) => configOptions.find((c) => c.id === id)?.title ?? id)
    .join(", ");

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="grid grid-cols-2 items-center gap-2 rounded-md border bg-background px-2 py-2"
    >
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="cursor-grab touch-none text-muted-foreground h-6 w-6 shrink-0"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </Button>
        <Input
          placeholder={t("Program name")}
          value={program.title}
          onChange={(e) => onTitleChange(programId, e.target.value)}
        />
      </div>

      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="flex-1 justify-between font-normal">
              <span className={selectedLabels ? "" : "text-muted-foreground"}>
                {selectedLabels || t("Select Vital Thresholds")}
              </span>
              <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[200px]" align="start">
            <Command>
              <CommandInput placeholder={t("Search...")} />
              <CommandList>
                <CommandEmpty>{t("No configurations found.")}</CommandEmpty>
                <CommandGroup>
                  {configOptions.map((option) => (
                    <CommandItem key={option.id} value={option.title} onSelect={() => toggleConfig(option.id)}>
                      <Check
                        className={`mr-2 h-4 w-4 ${
                          program.configurationIds.includes(option.id) ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      {option.title}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground h-7 w-7 shrink-0"
          onClick={() => onRemove(programId)}
          aria-label={t("Remove program")}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
